import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Card, CardMedia, Grid, Link, makeStyles, Typography } from '@material-ui/core';

import { generateLink } from 'Links';
import { useContent } from 'hooks';

import { ReactionCount, ShareCount } from 'features/content/widgets';
import { isPhotoOnly, isUrl, isVideoOnly } from 'features/content/helpers';
import { Body, UrlMetadata } from 'features/activity/components';
import { IMAGE_SIZE } from 'features/search/constants';
import { Buttons, Details, Images } from './index';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    borderRadius: theme.spacing(2),
    border: 'none',
  },
  content: {
    margin: theme.spacing(1),
  },
}));

const PostCard: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const content = useContent();
  const { content_id, content_object, user } = content;

  const { title } = content_object;

  function renderCounts() {
    const { reaction_count, comment_count, share_count } = content;

    if (!reaction_count && !comment_count && !share_count) {
      return null;
    }

    return (
      <Grid container spacing={1}>
        <Grid item>
          <ReactionCount variant="body1" color="textSecondary" />
        </Grid>
        <Grid item>
          <ShareCount variant="body1" color="textSecondary" />
        </Grid>
      </Grid>
    );
  }

  function renderTitle() {
    if (title) {
      return title;
    }

    if (isPhotoOnly(content)) {
      return t('search:imagePost');
    }

    if (isVideoOnly(content)) {
      return t('search:videoPost');
    }

    return null;
  }

  return (
    <Card className={classes.card}>
      <Link component={generateLink(`/content/${content_id}`)}>
        <CardMedia style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}>
          <Images />
        </CardMedia>
      </Link>

      <Grid container spacing={1} justify="center" alignItems="center" className={classes.content}>
        <Grid item xs={9}>
          <Grid container direction="column" justify="center">
            {user && (
              <Fragment>
                <Grid item>
                  <Link underline="none" component={generateLink(`/profile/${user?.id}`)}>
                    <Typography variant="body2">{user?.full_name}</Typography>
                  </Link>
                </Grid>

                <Body content={content} variant="body1" color="textSecondary" />
              </Fragment>
            )}

            <Grid item>
              <Box marginTop={0.5} marginBottom={0.5}>
                {isUrl(content) && (
                  <UrlMetadata
                    content={content}
                    variant="h6"
                    disableDescription={!!content_object.body}
                    color="textSecondary"
                  />
                )}
              </Box>
            </Grid>

            <Grid item>
              <Link component={generateLink(`/content/${content_id}`)}>
                <Typography variant="h6">{renderTitle()}</Typography>
              </Link>
            </Grid>

            <Grid item>
              <Details />
            </Grid>

            <Grid item>{renderCounts()}</Grid>
          </Grid>
        </Grid>
        <Grid item xs={3} container justify="center" alignItems="center">
          <Buttons />
        </Grid>
      </Grid>
    </Card>
  );
};

export default PostCard;
